import { useEffect, useState, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  makeStyles,
  Chip,
  LinearProgress,
  Box,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@material-ui/core';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  healthChip: {
    fontWeight: 600,
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  healthUp: {
    backgroundColor: '#4caf50',
    color: '#fff',
  },
  healthDown: {
    backgroundColor: '#f44336',
    color: '#fff',
  },
  healthWarn: {
    backgroundColor: '#ff9800',
    color: '#fff',
  },
  clusterCard: {
    height: '100%',
    border: `1px solid ${theme.palette.divider}`,
  },
  clusterTitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
    marginBottom: theme.spacing(1),
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
  },
  metricValue: {
    fontSize: '1.6rem',
    fontWeight: 700,
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    marginTop: 2,
  },
  metricCard: {
    textAlign: 'center',
    padding: theme.spacing(0.5),
  },
  syncSection: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
  },
  deptTable: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase' as const,
      padding: '8px 12px',
      backgroundColor: theme.palette.type === 'dark' ? '#333' : '#f5f5f5',
    },
    '& td': {
      fontSize: '0.85rem',
      padding: '6px 12px',
    },
  },
  eventRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    fontSize: '0.8rem',
  },
  eventDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  eventTime: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    minWidth: 50,
  },
  summaryCard: {
    textAlign: 'center',
    padding: theme.spacing(1),
    height: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
  },
  lastUpdated: {
    color: theme.palette.text.disabled,
    fontSize: '0.7rem',
    textAlign: 'right' as const,
    marginTop: theme.spacing(1),
  },
  errorService: {
    color: '#f44336',
    fontWeight: 600,
    fontSize: '0.8rem',
  },
}));

// ─── Prometheus query helper ───
interface MetricResult {
  value: number | null;
  error?: string;
}

interface VectorResult {
  metric: Record<string, string>;
  value: [number, string];
}

async function queryPrometheus(
  fetchApi: { fetch: typeof fetch },
  query: string,
): Promise<MetricResult> {
  try {
    const url = `/api/proxy/prometheus/api/v1/query?query=${encodeURIComponent(query)}`;
    const resp = await fetchApi.fetch(url);
    if (!resp.ok) return { value: null, error: `HTTP ${resp.status}` };
    const json = await resp.json();
    if (json.status !== 'success' || !json.data?.result?.length)
      return { value: 0 };
    const val = parseFloat(json.data.result[0].value[1]);
    return { value: isNaN(val) ? null : val };
  } catch (e: unknown) {
    return { value: null, error: String(e) };
  }
}

async function queryPrometheusVector(
  fetchApi: { fetch: typeof fetch },
  query: string,
): Promise<VectorResult[]> {
  try {
    const url = `/api/proxy/prometheus/api/v1/query?query=${encodeURIComponent(query)}`;
    const resp = await fetchApi.fetch(url);
    if (!resp.ok) return [];
    const json = await resp.json();
    if (json.status !== 'success') return [];
    return json.data?.result ?? [];
  } catch {
    return [];
  }
}

async function queryLoki(
  fetchApi: { fetch: typeof fetch },
  query: string,
): Promise<number | null> {
  try {
    const url = `/api/proxy/loki/loki/api/v1/query?query=${encodeURIComponent(query)}`;
    const resp = await fetchApi.fetch(url);
    if (!resp.ok) return null;
    const json = await resp.json();
    if (json.status !== 'success' || !json.data?.result?.length) return 0;
    const val = parseFloat(json.data.result[0].value[1]);
    return isNaN(val) ? null : val;
  } catch {
    return null;
  }
}

// ─── Types ───
interface ClusterMetrics {
  cpu: number | null;
  memory: number | null;
  nodes: number | null;
  pods: number | null;
  podCapacity: number | null;
}

interface ArgoSyncDetail {
  synced: number;
  outOfSync: number;
  degraded: number;
  unknown: number;
  outOfSyncApps: string[];
  degradedApps: string[];
}

interface DeptRow {
  dept: string;
  services: number;
  pods: number;
  cpu: number;
  errors5xx: number;
  quotaPct: number; // 부서 ns ResourceQuota 최대 사용률 (%)
}

interface EventItem {
  time: string;
  message: string;
  level: 'error' | 'warn' | 'info';
}

interface ObsSummary {
  logsPerHour: number | null;
  errorRate: number | null;
  tracesPerMin: number | null;
}

interface ErrorService {
  name: string;
  count: number;
}

interface BackupItem {
  name: string;
  schedule: string;
  lastRun: string;
  status: 'success' | 'failed' | 'suspended' | 'unknown';
  suspended: boolean;
}

interface DashboardState {
  // Health
  portalUp: MetricResult;
  keycloakUp: MetricResult;
  lokiUp: MetricResult;
  // Clusters
  system: ClusterMetrics;
  service: ClusterMetrics;
  // ArgoCD
  argo: ArgoSyncDetail;
  // Departments
  depts: DeptRow[];
  // Events
  events: EventItem[];
  // Observability
  obs: ObsSummary;
  // Top error services
  topErrors: ErrorService[];
  // Backup status
  backups: BackupItem[];
}

const EMPTY: DashboardState = {
  portalUp: { value: null },
  keycloakUp: { value: null },
  lokiUp: { value: null },
  system: { cpu: null, memory: null, nodes: null, pods: null, podCapacity: null },
  service: { cpu: null, memory: null, nodes: null, pods: null, podCapacity: null },
  argo: { synced: 0, outOfSync: 0, degraded: 0, unknown: 0, outOfSyncApps: [], degradedApps: [] },
  depts: [],
  events: [],
  obs: { logsPerHour: null, errorRate: null, tracesPerMin: null },
  topErrors: [],
  backups: [],
};

// ─── Sub-components ───
function HealthChipEx({
  label,
  value,
  detail,
  classes,
}: {
  label: string;
  value: MetricResult;
  detail?: string;
  classes: ReturnType<typeof useStyles>;
}) {
  const isUp = value.value !== null && value.value >= 1;
  const text = value.value === null ? `${label}: -` : `${label}: ${isUp ? 'UP' : 'DOWN'}`;
  const detailText = detail ? ` (${detail})` : '';
  return (
    <Tooltip title={detailText || text}>
      <Chip
        label={text}
        size="small"
        className={`${classes.healthChip} ${
          value.value === null ? '' : isUp ? classes.healthUp : classes.healthDown
        }`}
        variant={value.value === null ? 'outlined' : 'default'}
      />
    </Tooltip>
  );
}

function UsageBar({
  value,
  label,
  classes,
}: {
  value: number | null;
  label: string;
  classes: ReturnType<typeof useStyles>;
}) {
  const pct = value !== null ? Math.min(Math.round(value), 100) : 0;
  const barColor = pct > 80 ? '#f44336' : pct > 60 ? '#ff9800' : '#4caf50';
  return (
    <Box mb={0.5}>
      <Box display="flex" justifyContent="space-between" mb={0.3}>
        <Typography variant="caption" color="textSecondary">
          {label}
        </Typography>
        <Typography variant="caption" style={{ fontWeight: 600 }}>
          {value !== null ? `${pct}%` : '-'}
        </Typography>
      </Box>
      <Tooltip title={`${pct}%`}>
        <LinearProgress
          variant="determinate"
          value={pct}
          className={classes.progressBar}
          style={{ backgroundColor: '#e0e0e0' }}
        />
      </Tooltip>
      <style>{`.MuiLinearProgress-bar[aria-valuenow="${pct}"] { background-color: ${barColor} !important; }`}</style>
    </Box>
  );
}

function ClusterCard({
  title,
  data,
  classes,
}: {
  title: string;
  data: ClusterMetrics;
  classes: ReturnType<typeof useStyles>;
}) {
  return (
    <Card variant="outlined" className={classes.clusterCard}>
      <CardContent style={{ padding: 12 }}>
        <Typography className={classes.clusterTitle}>{title}</Typography>
        <UsageBar value={data.cpu} label="CPU" classes={classes} />
        <UsageBar value={data.memory} label="Memory" classes={classes} />
        <Box display="flex" justifyContent="space-between" mt={1}>
          <Typography variant="caption" color="textSecondary">
            Nodes: <strong>{data.nodes ?? '-'}</strong>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Pods: <strong>{data.pods ?? '-'}</strong>
            {data.podCapacity ? ` / ${data.podCapacity}` : ''}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Main component ───
export function PlatformOverviewCard() {
  const classes = useStyles();
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<DashboardState>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState('');


  const fetchAll = useCallback(async () => {
    // ─── 1. Health checks ───
    const [portalUp, keycloakUp, lokiUp] = await Promise.all([
      queryPrometheus(fetchApi, 'up{job="otel-collector"}'),
      queryPrometheus(
        fetchApi,
        'clamp_max(count(count by (pod) (container_cpu_usage_seconds_total{namespace=~"arcus-.*", pod=~"keycloak.*", container!=""})), 1) or vector(0)',
      ),
      queryPrometheus(
        fetchApi,
        'clamp_max(count(count by (pod) (container_cpu_usage_seconds_total{namespace="arcus-observability", pod=~"loki.*", container!=""})), 1) or vector(0)',
      ),
    ]);

    // ─── 2. Cluster metrics (System vs Service) ───
    // Node instance patterns (from kubectl get nodes / Prometheus 'instance' label):
    // System: gke-e1-arcus01-dev-a-*  (Standard GKE)
    // Service01: gk3-e1-arcus01-dev-apne3-servi-*  (Autopilot GKE)
    // NOTE: Prometheus uses 'instance' label (not 'node') for machine/container metrics
    const sysNodePattern = 'gke-e1-arcus01-dev-a-.*';
    const svcNodePattern = 'gk3-e1-arcus01-dev-apne3-servi-.*';

    const [
      sysCpu, sysMem, sysNodes, sysPods,
      svcCpu, svcMem, svcNodes, svcPods,
    ] = await Promise.all([
      // System CPU%: all pods on system nodes / system node cores
      queryPrometheus(fetchApi,
        `sum(rate(container_cpu_usage_seconds_total{instance=~"${sysNodePattern}", container!="", container!="POD"}[5m])) / sum(machine_cpu_cores{instance=~"${sysNodePattern}"}) * 100`),
      // System MEM%
      queryPrometheus(fetchApi,
        `sum(container_memory_working_set_bytes{instance=~"${sysNodePattern}", container!="", container!="POD"}) / sum(machine_memory_bytes{instance=~"${sysNodePattern}"}) * 100`),
      // System nodes: count distinct instances with machine_cpu_cores
      queryPrometheus(fetchApi,
        `count(count by (instance) (machine_cpu_cores{instance=~"${sysNodePattern}"}))`),
      // System pods
      queryPrometheus(fetchApi,
        `count(count by (pod) (container_cpu_usage_seconds_total{instance=~"${sysNodePattern}", container!="", container!="POD"}))`),
      // Service01 CPU%
      queryPrometheus(fetchApi,
        `sum(rate(container_cpu_usage_seconds_total{instance=~"${svcNodePattern}", container!="", container!="POD"}[5m])) / sum(machine_cpu_cores{instance=~"${svcNodePattern}"}) * 100`),
      // Service01 MEM%
      queryPrometheus(fetchApi,
        `sum(container_memory_working_set_bytes{instance=~"${svcNodePattern}", container!="", container!="POD"}) / sum(machine_memory_bytes{instance=~"${svcNodePattern}"}) * 100`),
      // Service01 nodes
      queryPrometheus(fetchApi,
        `count(count by (instance) (machine_cpu_cores{instance=~"${svcNodePattern}"}))`),
      // Service01 pods
      queryPrometheus(fetchApi,
        `count(count by (pod) (container_cpu_usage_seconds_total{instance=~"${svcNodePattern}", container!="", container!="POD"}))`),
    ]);

    // ─── 3. ArgoCD sync detail ───
    const [argoSyncedVec, argoOutOfSyncVec, argoDegradedVec] = await Promise.all([
      queryPrometheusVector(fetchApi, 'argocd_app_info{sync_status="Synced"}'),
      queryPrometheusVector(fetchApi, 'argocd_app_info{sync_status="OutOfSync"}'),
      queryPrometheusVector(fetchApi, 'argocd_app_info{health_status="Degraded"}'),
    ]);

    const argo: ArgoSyncDetail = {
      synced: argoSyncedVec.length,
      outOfSync: argoOutOfSyncVec.length,
      degraded: argoDegradedVec.length,
      unknown: 0,
      outOfSyncApps: argoOutOfSyncVec.map(r => r.metric.name).filter(Boolean),
      degradedApps: argoDegradedVec.map(r => r.metric.name).filter(Boolean),
    };

    // ─── 4. Department breakdown ───
    const deptNamespaces = ['ts', 'dm', 'bs1', 'bc', 'bi'];
    const deptResults = await Promise.all(
      deptNamespaces.map(async dept => {
        const nsPattern = `svc-${dept}|svc-${dept}-dev`;
        const [pods, cpu, errors5xx, quota] = await Promise.all([
          queryPrometheus(fetchApi,
            `count(count by (pod) (container_cpu_usage_seconds_total{namespace=~"${nsPattern}", container!="", container!="POD"})) or vector(0)`),
          queryPrometheus(fetchApi,
            `sum(rate(container_cpu_usage_seconds_total{namespace=~"${nsPattern}", container!="", container!="POD"}[5m])) * 1000 or vector(0)`),
          queryPrometheus(fetchApi,
            `sum(increase(http_server_requests_seconds_count{namespace=~"${nsPattern}", status=~"5.."}[1h])) or vector(0)`),
          queryPrometheus(fetchApi,
            `max(kube_resourcequota{namespace=~"${nsPattern}", type="used"} / ignoring(type) (kube_resourcequota{namespace=~"${nsPattern}", type="hard"} > 0)) * 100 or vector(0)`),
        ]);

        // Count distinct services (deployments) by unique pod prefixes
        const podVec = await queryPrometheusVector(fetchApi,
          `count by (pod) (container_cpu_usage_seconds_total{namespace=~"${nsPattern}", container!="", container!="POD"})`);
        const deployments = new Set(podVec.map(r => {
          const name = r.metric.pod || '';
          // Strip suffix like -abc1234-xyz12
          return name.replace(/-[a-f0-9]+-[a-z0-9]+$/, '').replace(/-[a-f0-9]{8,10}-[a-z0-9]{5}$/, '');
        }));

        return {
          dept: dept.toUpperCase(),
          services: deployments.size,
          pods: pods.value ?? 0,
          cpu: Math.round((cpu.value ?? 0) * 10) / 10,
          errors5xx: Math.round(errors5xx.value ?? 0),
          quotaPct: Math.round(quota.value ?? 0),
        };
      }),
    );

    // Filter out depts with no pods
    const depts = deptResults.filter(d => d.pods > 0);

    // ─── 5. Recent events (OOMKilled, CrashLoop, restarts) ───
    const events: EventItem[] = [];

    // Pod restarts
    const restartVec = await queryPrometheusVector(fetchApi,
      'increase(kube_pod_container_status_restarts_total[1h]) > 0');
    for (const r of restartVec.slice(0, 3)) {
      const pod = r.metric.pod || 'unknown';
      const ns = r.metric.namespace || '';
      const count = Math.round(parseFloat(r.value[1]));
      events.push({
        time: '',
        message: `${pod} (${ns}) — ${count} restart(s) in 1h`,
        level: count >= 3 ? 'error' : 'warn',
      });
    }

    // OOMKilled
    const oomVec = await queryPrometheusVector(fetchApi,
      'kube_pod_container_status_last_terminated_reason{reason="OOMKilled"} == 1');
    for (const r of oomVec.slice(0, 2)) {
      events.push({
        time: '',
        message: `${r.metric.pod || 'unknown'} OOMKilled (${r.metric.namespace || ''})`,
        level: 'error',
      });
    }

    // If no problems, add a positive event
    if (events.length === 0) {
      events.push({ time: '', message: 'No issues detected in the last hour', level: 'info' });
    }

    // ─── 6. Observability summary ───
    // Logs: query Loki directly (Prometheus doesn't scrape Loki self-metrics)
    // Traces: use processedSpans_total from Tempo metrics-generator
    const [logsCount, errorRate, tracesPerMin] = await Promise.all([
      queryLoki(fetchApi, 'sum(count_over_time({namespace=~".+"}[1h]))'),
      queryPrometheus(fetchApi,
        'sum(rate(http_server_requests_seconds_count{status=~"5.."}[5m])) / sum(rate(http_server_requests_seconds_count[5m])) * 100 or vector(0)'),
      queryPrometheus(fetchApi,
        'sum(rate(processedSpans_total[5m])) * 60'),
    ]);
    const logsPerHour: MetricResult = { value: logsCount };

    // ─── 7. Top error services ───
    const errorSvcVec = await queryPrometheusVector(fetchApi,
      'topk(5, sum by (service_name) (increase(http_server_requests_seconds_count{status=~"5.."}[1h])) > 0)');
    const topErrors: ErrorService[] = errorSvcVec.map(r => ({
      name: r.metric.service_name || 'unknown',
      count: Math.round(parseFloat(r.value[1])),
    })).sort((a, b) => b.count - a.count);

    // ─── 8. Backup status ───
    const backupDefs = [
      { name: 'PG Prd', cronjob: 'arcus-pg-backup-prd', ns: 'arcus-system', schedule: '02:00' },
      { name: 'Keycloak', cronjob: 'arcus-keycloak-backup', ns: 'arcus-system', schedule: '03:00' },
      { name: 'H2 Prd', cronjob: 'svc-h2-backup-prd', ns: 'svc-ts', schedule: '04:00' },
      { name: 'PXC workday-main', cronjob: 'workday-db-main-backup', ns: 'svc-bs', schedule: '01:00' },
      { name: 'PXC workday-wdc', cronjob: 'workday-db-wdc-backup', ns: 'svc-bs', schedule: '01:00' },
      { name: 'PXC sales-crm', cronjob: 'sales-crm-db-backup', ns: 'svc-ts', schedule: '01:00' },
    ];

    const [suspendVec, lastScheduleVec, failedVec] = await Promise.all([
      queryPrometheusVector(fetchApi,
        'kube_cronjob_spec_suspend{namespace=~"arcus-system|svc-ts|svc-bs", cronjob=~"arcus-pg-backup.*|arcus-keycloak-backup.*|svc-h2-backup.*|workday-db-.*-backup|sales-crm-db-backup"}'),
      queryPrometheusVector(fetchApi,
        'kube_cronjob_status_last_schedule_time{namespace=~"arcus-system|svc-ts|svc-bs", cronjob=~"arcus-pg-backup.*|arcus-keycloak-backup.*|svc-h2-backup.*|workday-db-.*-backup|sales-crm-db-backup"}'),
      queryPrometheusVector(fetchApi,
        'kube_job_status_failed{namespace=~"arcus-system|svc-ts|svc-bs", job_name=~"arcus-pg-backup.*|arcus-keycloak-backup.*|svc-h2-backup.*|workday-db-.*-backup.*|sales-crm-db-backup.*"} > 0'),
    ]);

    const suspendMap = new Map(suspendVec.map(r => [`${r.metric.namespace}/${r.metric.cronjob}`, parseFloat(r.value[1])]));
    const scheduleMap = new Map(lastScheduleVec.map(r => [`${r.metric.namespace}/${r.metric.cronjob}`, parseFloat(r.value[1])]));
    const failedJobs = new Set(failedVec.map(r => r.metric.job_name?.replace(/-\d+$/, '') || ''));

    const backups: BackupItem[] = backupDefs.map(def => {
      const key = `${def.ns}/${def.cronjob}`;
      const isSuspended = suspendMap.get(key) === 1;
      const lastTs = scheduleMap.get(key);
      let lastRun = '—';
      if (lastTs && lastTs > 0) {
        const ago = Math.round((Date.now() / 1000 - lastTs) / 3600);
        lastRun = ago < 1 ? '<1h ago' : `${ago}h ago`;
      }
      let status: BackupItem['status'] = 'unknown';
      if (isSuspended) {
        status = 'suspended';
      } else if (failedJobs.has(def.cronjob)) {
        status = 'failed';
      } else if (lastTs && lastTs > 0) {
        status = 'success';
      }
      return { name: def.name, schedule: def.schedule, lastRun, status, suspended: isSuspended };
    });

    // ─── Set state ───
    setState({
      portalUp,
      keycloakUp,
      lokiUp,
      system: {
        cpu: sysCpu.value,
        memory: sysMem.value,
        nodes: sysCpu.value != null ? Math.round(sysNodes.value ?? 0) : null,
        pods: sysCpu.value != null ? Math.round(sysPods.value ?? 0) : null,
        podCapacity: null,
      },
      service: {
        cpu: svcCpu.value,
        memory: svcMem.value,
        nodes: svcCpu.value != null ? Math.round(svcNodes.value ?? 0) : null,
        pods: svcCpu.value != null ? Math.round(svcPods.value ?? 0) : null,
        podCapacity: null,
      },
      argo,
      depts,
      events,
      obs: {
        logsPerHour: logsPerHour.value,
        errorRate: errorRate.value,
        tracesPerMin: tracesPerMin.value,
      },
      topErrors,
      backups,
    });

    setLoading(false);
    setLastUpdate(new Date().toLocaleTimeString('ko-KR'));
  }, [fetchApi]);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  if (loading) return <LinearProgress />;

  const totalApps = state.argo.synced + state.argo.outOfSync + state.argo.degraded + state.argo.unknown;

  return (
    <div>
      {/* ── Section 1: Component Health ── */}
      <Typography className={classes.sectionTitle}>Component Health</Typography>
      <Box mb={2}>
        <HealthChipEx label="Portal" value={state.portalUp} classes={classes} />
        <HealthChipEx label="Keycloak" value={state.keycloakUp} classes={classes} />
        <HealthChipEx label="Loki" value={state.lokiUp} classes={classes} />
        <Chip
          label={`ArgoCD: ${state.argo.synced}/${totalApps} Synced`}
          size="small"
          className={classes.healthChip}
          style={{
            backgroundColor:
              state.argo.outOfSync > 0 || state.argo.degraded > 0 ? '#ff9800' : '#4caf50',
            color: '#fff',
            fontWeight: 600,
          }}
        />
      </Box>

      {/* ── Section 2: Cluster Resources ── */}
      <Typography className={classes.sectionTitle}>Cluster Resources</Typography>
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={6}>
          <ClusterCard
            title={`System Cluster (${state.system.nodes ?? '-'} nodes)`}
            data={state.system}
            classes={classes}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ClusterCard
            title={`Service01 Cluster (${state.service.nodes ?? '-'} nodes)`}
            data={state.service}
            classes={classes}
          />
        </Grid>
      </Grid>

      {/* ── Section 3: ArgoCD Sync Status ── */}
      <Typography className={classes.sectionTitle}>ArgoCD Sync Status</Typography>
      <Box className={classes.syncSection} mb={2}>
        <Box display="flex" gap={1} flexWrap="wrap" mb={state.argo.outOfSyncApps.length > 0 || state.argo.degradedApps.length > 0 ? 1 : 0}>
          <Chip
            label={`Synced: ${state.argo.synced}`}
            size="small"
            style={{ backgroundColor: '#4caf50', color: '#fff', fontWeight: 600, marginRight: 4 }}
          />
          {state.argo.outOfSync > 0 && (
            <Chip
              label={`OutOfSync: ${state.argo.outOfSync}`}
              size="small"
              style={{ backgroundColor: '#ff9800', color: '#fff', fontWeight: 600, marginRight: 4 }}
            />
          )}
          {state.argo.degraded > 0 && (
            <Chip
              label={`Degraded: ${state.argo.degraded}`}
              size="small"
              style={{ backgroundColor: '#f44336', color: '#fff', fontWeight: 600, marginRight: 4 }}
            />
          )}
          {state.argo.outOfSync === 0 && state.argo.degraded === 0 && (
            <Chip
              label="All Healthy"
              size="small"
              variant="outlined"
              style={{ color: '#4caf50', borderColor: '#4caf50', fontWeight: 600 }}
            />
          )}
        </Box>
        {state.argo.outOfSyncApps.length > 0 && (
          <Typography variant="caption" style={{ color: '#ff9800' }}>
            OutOfSync: {state.argo.outOfSyncApps.join(', ')}
          </Typography>
        )}
        {state.argo.degradedApps.length > 0 && (
          <Typography variant="caption" style={{ color: '#f44336', display: 'block' }}>
            Degraded: {state.argo.degradedApps.join(', ')}
          </Typography>
        )}
      </Box>

      {/* ── Section 4: Department Services ── */}
      {state.depts.length > 0 && (
        <>
          <Typography className={classes.sectionTitle}>Department Services</Typography>
          <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 16 }}>
            <Table size="small" className={classes.deptTable}>
              <TableHead>
                <TableRow>
                  <TableCell>Dept</TableCell>
                  <TableCell align="center">Services</TableCell>
                  <TableCell align="center">Pods</TableCell>
                  <TableCell align="center">CPU (m)</TableCell>
                  <TableCell align="center">Quota</TableCell>
                  <TableCell align="center">5xx Errors</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.depts.map(d => (
                  <TableRow key={d.dept}>
                    <TableCell style={{ fontWeight: 600 }}>{d.dept}</TableCell>
                    <TableCell align="center">{d.services}</TableCell>
                    <TableCell align="center">{d.pods}</TableCell>
                    <TableCell align="center">{d.cpu}</TableCell>
                    <TableCell
                      align="center"
                      style={{
                        color:
                          d.quotaPct >= 95
                            ? '#f44336'
                            : d.quotaPct >= 80
                              ? '#ff9800'
                              : 'inherit',
                        fontWeight: d.quotaPct >= 80 ? 600 : 400,
                      }}
                    >
                      {d.quotaPct}%
                    </TableCell>
                    <TableCell
                      align="center"
                      style={{
                        color: d.errors5xx > 0 ? '#f44336' : 'inherit',
                        fontWeight: d.errors5xx > 0 ? 600 : 400,
                      }}
                    >
                      {d.errors5xx}
                    </TableCell>
                    <TableCell align="center">
                      {d.errors5xx > 10 ? '🔴' : d.errors5xx > 0 ? '🟡' : '🟢'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ── Section 5: Recent Events ── */}
      <Typography className={classes.sectionTitle}>Recent Events</Typography>
      <Box
        mb={2}
        p={1.5}
        style={{ border: '1px solid', borderColor: 'rgba(255,255,255,0.12)', borderRadius: 4 }}
      >
        {state.events.map((evt, i) => (
          <div key={i} className={classes.eventRow}>
            <div
              className={classes.eventDot}
              style={{
                backgroundColor:
                  evt.level === 'error' ? '#f44336' : evt.level === 'warn' ? '#ff9800' : '#4caf50',
              }}
            />
            <Typography variant="caption" style={{ flex: 1 }}>
              {evt.message}
            </Typography>
          </div>
        ))}
      </Box>

      {/* ── Section 6: Observability Summary ── */}
      <Typography className={classes.sectionTitle}>Observability</Typography>
      <Grid container spacing={1} style={{ marginBottom: 16 }}>
        <Grid item xs={4}>
          <Card variant="outlined" className={classes.summaryCard}>
            <CardContent style={{ padding: 8 }}>
              <Typography className={classes.metricValue} style={{ color: '#1976d2' }}>
                {state.obs.logsPerHour !== null
                  ? state.obs.logsPerHour >= 1_000_000
                    ? `${(state.obs.logsPerHour / 1_000_000).toFixed(1)}M`
                    : state.obs.logsPerHour >= 1_000
                      ? `${(state.obs.logsPerHour / 1_000).toFixed(1)}K`
                      : `${Math.round(state.obs.logsPerHour)}`
                  : '-'}
              </Typography>
              <Typography className={classes.metricLabel}>Logs/hour</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card variant="outlined" className={classes.summaryCard}>
            <CardContent style={{ padding: 8 }}>
              <Typography
                className={classes.metricValue}
                style={{
                  color:
                    state.obs.errorRate !== null && state.obs.errorRate > 1
                      ? '#f44336'
                      : '#4caf50',
                }}
              >
                {state.obs.errorRate !== null ? `${state.obs.errorRate.toFixed(1)}%` : '-'}
              </Typography>
              <Typography className={classes.metricLabel}>Error Rate (5xx)</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card variant="outlined" className={classes.summaryCard}>
            <CardContent style={{ padding: 8 }}>
              <Typography className={classes.metricValue} style={{ color: '#7c4dff' }}>
                {state.obs.tracesPerMin !== null
                  ? Math.round(state.obs.tracesPerMin).toLocaleString()
                  : '-'}
              </Typography>
              <Typography className={classes.metricLabel}>Traces/min</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Section 7: Top Error Services ── */}
      {state.topErrors.length > 0 && (
        <>
          <Typography className={classes.sectionTitle}>Top Error Services (1h)</Typography>
          <Box mb={2} p={1} style={{ border: '1px solid', borderColor: 'rgba(255,255,255,0.12)', borderRadius: 4 }}>
            {state.topErrors.map((svc, i) => (
              <Box key={svc.name} display="flex" justifyContent="space-between" py={0.3}>
                <Typography variant="caption">
                  {i + 1}. {svc.name}
                </Typography>
                <Typography variant="caption" className={classes.errorService}>
                  {svc.count} errors
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* ── Section 8: Backup Status ── */}
      <Typography className={classes.sectionTitle}>Backup Status</Typography>
      <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 16 }}>
        <Table size="small" className={classes.deptTable}>
          <TableHead>
            <TableRow>
              <TableCell>Backup</TableCell>
              <TableCell align="center">Schedule</TableCell>
              <TableCell align="center">Last Run</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.backups.map(b => (
              <TableRow key={b.name}>
                <TableCell style={{ fontWeight: 600 }}>{b.name}</TableCell>
                <TableCell align="center">{b.schedule}</TableCell>
                <TableCell align="center">{b.lastRun}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={
                      b.status === 'success' ? 'OK' :
                      b.status === 'failed' ? 'FAIL' :
                      b.status === 'suspended' ? 'OFF' : '—'
                    }
                    size="small"
                    style={{
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 20,
                      backgroundColor:
                        b.status === 'success' ? '#4caf50' :
                        b.status === 'failed' ? '#f44336' :
                        b.status === 'suspended' ? '#9e9e9e' : '#e0e0e0',
                      color: b.status === 'unknown' ? '#666' : '#fff',
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Footer ── */}
      <Divider style={{ marginBottom: 8 }} />
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="textSecondary">
          {(state.system.pods ?? 0) + (state.service.pods ?? 0)} pods |{' '}
          {state.argo.synced + state.argo.outOfSync + state.argo.degraded} apps |{' '}
          {state.depts.reduce((s, d) => s + d.errors5xx, 0)} errors
        </Typography>
      </Box>
      <Typography className={classes.lastUpdated}>
        Last updated: {lastUpdate} (30s auto-refresh)
      </Typography>
    </div>
  );
}
